import { useState } from 'react'
import { createPortal } from 'react-dom'
import { addDetail, CreateStockDispatchDetailPayload } from '@/services/api/stockDispatch'

interface AddTruckRegistrationModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  stockDispatchId?: string
}

const AddTruckRegistrationModal = ({
  isOpen,
  onClose,
  onSuccess,
  stockDispatchId,
}: AddTruckRegistrationModalProps) => {
  const [truckReg, setTruckReg] = useState('')
  const [transporter, setTransporter] = useState('')
  const [truckRate, setTruckRate] = useState('')
  const [dispatchedDate, setDispatchedDate] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stockDispatchId) {
      setError('Stock dispatch ID is required')
      return
    }
    if (!truckReg.trim()) {
      setError('Truck registration is required')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const payload: CreateStockDispatchDetailPayload = {
        truckReg: truckReg.trim(),
        transporter: transporter.trim() || undefined,
        truckRate: truckRate.trim() || undefined,
        dispatchedDate: dispatchedDate || undefined,
      }
      await addDetail(stockDispatchId, payload)
      setTruckReg('')
      setTransporter('')
      setTruckRate('')
      setDispatchedDate('')
      onSuccess?.()
      onClose()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add truck registration')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setTruckReg('')
    setTransporter('')
    setTruckRate('')
    setDispatchedDate('')
    setError(null)
    onClose()
  }

  if (!isOpen) return null

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      onClick={handleClose}
      style={{ zIndex: 80 }}
    >
      <div
        className="bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: 'Lexend Deca',
          width: '480px',
          maxWidth: '95vw',
          borderRadius: '12px',
          padding: '24px',
        }}
      >
        <h2
          style={{
            fontFamily: 'Lexend Deca',
            fontWeight: 600,
            fontSize: '18px',
            lineHeight: '26px',
            color: '#212126',
            marginBottom: '16px',
          }}
        >
          Add Truck Registration
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 500,
                fontSize: '14px',
                color: '#69697A',
                display: 'block',
                marginBottom: '8px',
              }}
            >
              Truck Registration *
            </label>
            <input
              type="text"
              value={truckReg}
              onChange={(e) => setTruckReg(e.target.value)}
              placeholder="e.g. ABC 123 GP"
              required
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              style={{
                fontFamily: 'Lexend Deca',
                fontSize: '12px',
                height: '40px',
                border: '1px solid #E9E9EA',
                borderRadius: '8px',
              }}
            />
          </div>

          <div>
            <label
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 500,
                fontSize: '14px',
                color: '#69697A',
                display: 'block',
                marginBottom: '8px',
              }}
            >
              Transporter
            </label>
            <input
              type="text"
              value={transporter}
              onChange={(e) => setTransporter(e.target.value)}
              placeholder="Enter transporter"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              style={{
                fontFamily: 'Lexend Deca',
                fontSize: '12px',
                height: '40px',
                border: '1px solid #E9E9EA',
                borderRadius: '8px',
              }}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontSize: '14px',
                  color: '#69697A',
                  display: 'block',
                  marginBottom: '8px',
                }}
              >
                Truck Rate
              </label>
              <input
                type="text"
                value={truckRate}
                onChange={(e) => setTruckRate(e.target.value)}
                placeholder="Enter rate"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontSize: '12px',
                  height: '40px',
                  border: '1px solid #E9E9EA',
                  borderRadius: '8px',
                }}
              />
            </div>
            <div>
              <label
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 500,
                  fontSize: '14px',
                  color: '#69697A',
                  display: 'block',
                  marginBottom: '8px',
                }}
              >
                Dispatched Date
              </label>
              <input
                type="date"
                value={dispatchedDate}
                onChange={(e) => setDispatchedDate(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                style={{
                  fontFamily: 'Lexend Deca',
                  fontSize: '12px',
                  height: '40px',
                  border: '1px solid #E9E9EA',
                  borderRadius: '8px',
                }}
              />
            </div>
          </div>

          {error && (
            <p style={{ fontFamily: 'Lexend Deca', fontSize: '13px', color: '#E93323' }}>{error}</p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="hover:opacity-90 transition-opacity"
              style={{
                fontFamily: 'Lexend Deca',
                fontSize: '14px',
                padding: '8px 16px',
                border: '1px solid #E9E9EA',
                borderRadius: '6px',
                backgroundColor: '#FFFFFF',
                color: '#212126',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="hover:opacity-90 transition-opacity disabled:opacity-60"
              style={{
                fontFamily: 'Lexend Deca',
                fontSize: '14px',
                padding: '8px 16px',
                borderRadius: '6px',
                backgroundColor: '#212126',
                color: '#FFFFFF',
              }}
            >
              {isSubmitting ? 'Saving...' : 'Add Truck'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default AddTruckRegistrationModal
